import { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../../../components/base/Button';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import { AreaIcon, BathroomIcon, BedroomIcon, ParkingIcon } from '../../../components/base/icons/MetricIcons';
import {
  getPropertyStatusColor,
  isPriceOnRequest,
  priceOnRequestLabel,
} from '../../../utils/propertyUtils';
import { formatAmount } from '../../../utils/numberFormat';
import { getPropertyTypeIconId } from '../../../config/propertyTypeCatalog';
import DomainIcon from '../../../components/base/icons/domain/DomainIcon';
import ListingBadges from '../../../components/property/ListingBadges';
import {
  getPrimaryDisplayAttributes,
  formatAttributeValue,
  type DisplayAttribute,
} from '../../../utils/propertyAttributeUtils';
import { useFavorites } from '../../../hooks/useFavorites';
import { useAuth } from '../../../hooks/useAuth';
import VirtualTourModal from '../../../components/feature/VirtualTourModal';
import ListPickerModal from '../../../components/favorites/ListPickerModal';
import { dismissSaveHint } from '../../../components/favorites/SaveHintTooltip';
import GoogleMap from '../../../components/base/GoogleMap';
import { getPropertyDisplayTitle } from '../../../utils/arabicPropertyTitle';
import { translateAmenity } from '../../../utils/amenityTranslations';
import { trackContact, trackShare } from '../../../services/analytics';
import type { Property } from '../../../mocks/properties';
import type { CSSProperties } from 'react';

interface PropertyCardProps {
  property: Property & { distanceFromLandmark?: number };
  onClick: () => void;
  motionOrder?: number;
  landmarkName?: string;
  eagerImage?: boolean;
}

function PropertyCard({ property, onClick, motionOrder = 0, landmarkName, eagerImage = false }: PropertyCardProps) {
  const { t, i18n } = useTranslation(['search', 'property', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isFavorite } = useFavorites();
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  // Guests must sign in to contact a broker (same gate as the property page).
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [activeTab, setActiveTab] = useState<'specs' | 'features' | 'agent' | 'location'>('features');
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  // Sign-in dialog: focus the primary action on open + Escape to close.
  const signInPrimaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showSignInPrompt) return;
    signInPrimaryRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setShowSignInPrompt(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showSignInPrompt]);

  // Placeholder image as subtle gradient (fallback when image fails to load)
  const placeholderGradient = 'linear-gradient(135deg, #E1E9E8 0%, #CAD6D4 50%, #E1E9E8 100%)';

  // Get all images for the property
  const propertyImages = useMemo(() => {
    const images: string[] = [];
    if (property.imageUrl) images.push(property.imageUrl);
    if (property.galleryImages?.length) {
      property.galleryImages.forEach((img: string) => {
        if (img && !images.includes(img)) images.push(img);
      });
    }
    return images.length > 0 ? images : [''];
  }, [property.imageUrl, property.galleryImages]);

  const handlePrevImage = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (propertyImages.length <= 1) return;
    setActiveImageIndex((prev) => (prev === 0 ? propertyImages.length - 1 : prev - 1));
  }, [propertyImages.length]);

  const handleNextImage = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (propertyImages.length <= 1) return;
    setActiveImageIndex((prev) => (prev === propertyImages.length - 1 ? 0 : prev + 1));
  }, [propertyImages.length]);

  const handleThumbnailClick = useCallback((index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImageIndex(index);
  }, []);

  const hasVirtualTour = property.hasVirtualTour && property.virtualTourUrl;
  const hasVideo = !!property.videoUrl;
  const hasFloorPlan = (property.floorPlanImages?.length ?? 0) > 0;
  const hasDistanceInfo = property.distanceFromLandmark !== undefined && landmarkName;

  const isRtl = i18n.dir() === 'rtl';
  const unitLabel = t('common:units.sqm');
  const currencyName = t('common:currency.name');

  const propertyIsFavorite = isFavorite(property.id);

  // Heart click handler - opens ListPickerModal (per CONTEXT.md - not instant toggle)
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      // Redirect to signin with return path
      navigate('/signin', {
        state: {
          from: location,
          message: 'signin_to_save_favorites'
        }
      });
      return;
    }

    // Open list picker modal (per CONTEXT.md decision)
    setShowListPicker(true);
  }, [user, navigate, location]);

  // Record a contact click; for guests, open the sign-in prompt and block the
  // action. Returns true only when the contact may proceed (signed in).
  const guardContact = useCallback((method: 'call' | 'whatsapp' | 'email'): boolean => {
    if (property.id) trackContact('property', property.id, method);
    if (!user) { setShowSignInPrompt(true); return false; }
    return true;
  }, [property.id, user]);

  // Share the listing — native share sheet when available, clipboard otherwise.
  const handleShareClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (property.id) trackShare('property', property.id, typeof navigator.share === 'function' ? 'native' : 'clipboard');
    const url = `${window.location.origin}/property/${property.id}`;
    const shareData = { title: property.title || 'Property', url };
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (typeof nav.share === 'function') {
      nav.share(shareData).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).catch(() => {});
    }
  }, [property.id, property.title]);

  // Handle ListPickerModal close - dismiss hint after first interaction
  const handleListPickerClose = useCallback(() => {
    setShowListPicker(false);
    // Dismiss hint after first save interaction
    dismissSaveHint();
  }, []);

  // Use language-appropriate fields for display
  const isArabic = i18n.language === 'ar';

  // Generate proper title based on language
  const title = useMemo(() => {
    return getPropertyDisplayTitle({
      title: property.title,
      titleAr: property.titleAr,
      propertyType: property.propertyType,
      listingType: property.listingType,
      city: property.city,
      cityAr: property.cityAr,
      bedrooms: property.bedrooms,
    }, isArabic);
  }, [property.title, property.titleAr, property.propertyType, property.listingType, property.city, property.cityAr, property.bedrooms, isArabic]);

  const city = useMemo(() => {
    if (isArabic) {
      return property.cityAr || property.city;
    }
    return property.city;
  }, [property.city, property.cityAr, isArabic]);

  const state = useMemo(() => {
    if (isArabic) {
      return property.stateAr || property.state;
    }
    return property.state;
  }, [property.state, property.stateAr, isArabic]);
  const statusColorClass = getPropertyStatusColor(property.status);
  const typeIconId = getPropertyTypeIconId(property.propertyType);

  const statusLabel = t(`search.statuses.${property.status}`, {
    defaultValue: property.status,
  });
  const typeLabel = t(`search.filters.propertyTypeOptions.${property.propertyType}`, {
    defaultValue: property.propertyType,
  });

  const onRequest = isPriceOnRequest(property.price);
  // Western digits with "," grouping in both languages (utils/numberFormat.ts).
  const formattedPrice = formatAmount(property.price);
  const priceAccessible = onRequest
    ? priceOnRequestLabel(i18n.dir() === 'rtl')
    : t('search.card.priceAccessible', {
        price: formattedPrice,
        currency: currencyName,
      });

  const areaSqm = Math.round(property.sqft / 10.7639);
  const formattedArea = formatAmount(areaSqm);
  const parkingSpaces = property.parkingSpaces ?? (property.parking ? 1 : 0);

  // Get dynamic attributes based on property type
  const displayAttributes = useMemo(() => {
    const propertyData = {
      propertyType: property.propertyType,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      sqft: property.sqft,
      area: property.sqft,
      parking: parkingSpaces,
      yearBuilt: property.yearBuilt,
      floors: property.floors,
      furnished: property.furnished,
      petFriendly: property.petFriendly,
      orientation: property.orientation,
      floorLevel: property.floorLevel,
      apartmentNumber: property.apartmentNumber,
      livingRooms: property.livingRooms,
    };
    return getPrimaryDisplayAttributes(propertyData, isArabic ? 'ar' : 'en', 6);
  }, [property, parkingSpaces, isArabic]);

  // Helper to get icon component/element for an attribute
  const getAttributeIcon = useCallback((attr: DisplayAttribute) => {
    switch (attr.key) {
      case 'bedrooms':
        return <BedroomIcon className="h-4 w-4" aria-hidden />;
      case 'bathrooms':
        return <BathroomIcon className="h-4 w-4" aria-hidden />;
      case 'area':
        return <AreaIcon className="h-4 w-4" aria-hidden />;
      case 'parking':
        return <ParkingIcon className="h-4 w-4" aria-hidden />;
      case 'floors':
        return <DomainIcon id="floors" size={16} className="shrink-0" />;
      default:
        // The icon sits inside `.spec-icon-mini`, a purple gradient chip with
        // `color: white`. Font icons size by font-size (not w/h), and the old
        // `text-[#004D40]` painted the glyph purple-on-purple → invisible (looked
        // like a solid dot). Drop the colour so it inherits white, and size it.
        return <i className={`${attr.icon} text-[15px] leading-none`} aria-hidden="true"></i>;
    }
  }, []);

  const isActionDisabled = property.status === 'sold' || property.status === 'rented';
  const actionLabel = t('search.card.viewDetails');

  const motionStyle = useMemo(
    () => ({ '--motion-delay': `${motionOrder * 75}ms` } as CSSProperties),
    [motionOrder],
  );

  const handleExpandToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const handleTabClick = useCallback((tab: 'specs' | 'features' | 'agent' | 'location', e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveTab(tab);
  }, []);

  return (
    <div className="relative" dir={i18n.dir()} style={{ overflow: 'visible' }}>
      <div
        ref={cardRef}
        className={`liquid-glass-card group cursor-pointer ${isExpanded ? 'is-expanded' : ''}`}
        style={motionStyle}
        data-reveal
        onClick={() => onClick()}
      >

      {/* Stretched anchor — gives the card a real href for keyboard activation,
          Cmd/middle-click and open-in-new-tab + SEO. The reliable click path is
          the card root's onClick (above), since the card's positioned content
          would otherwise sit above a low-z overlay. stopPropagation here prevents
          a double navigation when the (exposed) anchor itself is clicked; modified
          clicks fall through to native anchor behaviour (open in new tab). */}
      <Link
        to={`/property/${property.id}`}
        aria-label={title}
        className="absolute inset-0 z-0"
        onClick={(e) => {
          e.stopPropagation();
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
          e.preventDefault();
          onClick();
        }}
      />

      {/* Image Section */}
      <div className="card-image-section">
        {/* Main Image */}
        <div className="relative w-full h-full">
          {imageErrors.has(activeImageIndex) || !propertyImages[activeImageIndex] ? (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: placeholderGradient }}
            >
              <div className="text-slate-400 text-center p-4">
                <i className="ri-building-2-line text-5xl opacity-60"></i>
                <p className="text-sm mt-2 opacity-60">{i18n.language === 'ar' ? 'صورة العقار' : 'Property Image'}</p>
              </div>
            </div>
          ) : (
            <img
              src={propertyImages[activeImageIndex]}
              alt={`${title} - ${activeImageIndex + 1}`}
              loading={eagerImage ? "eager" : "lazy"}
              width={400}
              height={300}
              className="w-full h-full object-cover"
              onError={() => setImageErrors(prev => new Set(prev).add(activeImageIndex))}
            />
          )}

          {/* Image Navigation Arrows — only meaningful when there's more than
              one image. Previously rendered for any non-empty first image,
              which produced visible-but-dead arrows on single-image listings
              (the recurring "arrows don't work" bug — compounded by the backend
              returning only one image per card; backend now returns imageUrls[]).
              In RTL the gallery reads right-to-left, so the visually-left button
              advances to the next image and the visually-right button rewinds. */}
          {propertyImages.length > 1 && !imageErrors.has(0) && (() => {
            const isRtl = i18n.dir() === 'rtl';
            const leftButtonHandler  = isRtl ? handleNextImage : handlePrevImage;
            const rightButtonHandler = isRtl ? handlePrevImage : handleNextImage;
            const leftButtonAria  = isRtl
              ? (i18n.language === 'ar' ? 'الصورة التالية'  : 'Next image')
              : (i18n.language === 'ar' ? 'الصورة السابقة' : 'Previous image');
            const rightButtonAria = isRtl
              ? (i18n.language === 'ar' ? 'الصورة السابقة' : 'Previous image')
              : (i18n.language === 'ar' ? 'الصورة التالية'  : 'Next image');
            return (
              <>
                <button
                  type="button"
                  onClick={leftButtonHandler}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label={leftButtonAria}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '8px',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    color: '#324441',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    border: 'none',
                    cursor: 'pointer',
                    zIndex: 30,
                    padding: 0,
                  }}
                >
                  <i className="ri-arrow-left-s-line" style={{ fontSize: '18px', pointerEvents: 'none' }}></i>
                </button>
                <button
                  type="button"
                  onClick={rightButtonHandler}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label={rightButtonAria}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    color: '#324441',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    border: 'none',
                    cursor: 'pointer',
                    zIndex: 30,
                    padding: 0,
                  }}
                >
                  <i className="ri-arrow-right-s-line" style={{ fontSize: '18px', pointerEvents: 'none' }}></i>
                </button>
              </>
            );
          })()}
        </div>

        {/* Image Counter */}
        {propertyImages.length > 1 && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/95 text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg border border-white/50 flex items-center gap-1.5" style={{ zIndex: 25 }}>
            <i className="ri-image-line text-[#055A4C]"></i>
            <span dir="ltr" className="inline-block">{activeImageIndex + 1} / {propertyImages.length}</span>
          </div>
        )}

        {/* Thumbnail Strip at Bottom - Enhanced visibility */}
        {propertyImages.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3 z-10">
            <div className="flex gap-2 justify-center overflow-x-auto pb-1 hide-scrollbar">
              {propertyImages.slice(0, 6).map((img, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => handleThumbnailClick(index, e)}
                  className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all duration-200 ${
                    index === activeImageIndex
                      ? 'border-white shadow-lg scale-110 ring-2 ring-white/50'
                      : 'border-white/30 opacity-80 hover:opacity-100 hover:border-white/60 hover:scale-105'
                  }`}
                >
                  {imageErrors.has(index) || !img ? (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: placeholderGradient }}
                    >
                      <i className="ri-building-2-line text-slate-400 text-xs"></i>
                    </div>
                  ) : (
                    <img
                      src={img}
                      alt={i18n.language === 'ar' ? `صورة مصغّرة ${index + 1}` : `Thumbnail ${index + 1}`}
                      width={64}
                      height={48}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={() => setImageErrors(prev => new Set(prev).add(index))}
                    />
                  )}
                </button>
              ))}
              {propertyImages.length > 6 && (
                <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 border border-white/30">
                  +{propertyImages.length - 6}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compact Info Overlay (visible when collapsed) */}
        <div className="card-compact-info">
          {/* Top Row: Type Badge, Status & Favorite - Always visible */}
          <div className="top-row">
            {/* Property Type Badge - now also carries bed/bath counts so the
                key specs are visible on the card without expanding. The listing
                marks sit under it (utils/listingBadges.ts). */}
            <div className="flex flex-col items-start gap-1.5">
            <div className="bg-white/95 px-3 py-2 rounded-xl flex items-center gap-2 shadow-lg border border-white/50">
              <DomainIcon id={typeIconId} size={16} className="shrink-0 text-[#055A4C]" />
              <span className="text-gray-800 text-xs font-semibold">{typeLabel}</span>
              {(property.bedrooms > 0 || property.bathrooms > 0) && (
                <span className="mx-0.5 h-3.5 w-px bg-gray-300/80" aria-hidden="true" />
              )}
              {property.bedrooms > 0 && (
                <span
                  className="flex items-center gap-1 text-gray-800 text-xs font-semibold"
                  aria-label={t('search.card.bedrooms', { count: property.bedrooms, defaultValue: `${property.bedrooms} bedrooms` })}
                >
                  <BedroomIcon className="h-3.5 w-3.5 text-[#004D40]" aria-hidden />
                  {formatAmount(property.bedrooms)}
                </span>
              )}
              {property.bathrooms > 0 && (
                <span
                  className="flex items-center gap-1 text-gray-800 text-xs font-semibold"
                  aria-label={t('search.card.bathrooms', { count: property.bathrooms, defaultValue: `${property.bathrooms} bathrooms` })}
                >
                  <BathroomIcon className="h-3.5 w-3.5 text-[#004D40]" aria-hidden />
                  {formatAmount(property.bathrooms)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <ListingBadges listing={property} earnedLimit={1} />
            </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Status Badge - Enhanced */}
              {property.status !== 'available' && (
                <div className={`px-3 py-2 rounded-xl text-xs font-bold shadow-lg backdrop-blur-md ${statusColorClass}`}>
                  {statusLabel}
                </div>
              )}

              {/* Quick-view (expand details panel) — moved here from the bottom
                  corner so the bottom can hold the contact buttons + price. */}
              <button
                type="button"
                onClick={handleExpandToggle}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-lg border border-white/50 bg-white/95 hover:bg-white text-[#003930]"
                aria-label={i18n.language === 'ar' ? 'عرض سريع للتفاصيل' : 'Quick view details'}
                title={i18n.language === 'ar' ? 'عرض سريع للتفاصيل' : 'Quick view details'}
              >
                <i className="ri-eye-line text-xl"></i>
              </button>

              {/* Share Button */}
              <button
                onClick={handleShareClick}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-lg border border-white/50 bg-white/95 hover:bg-white"
                aria-label={t('search.card.share', { defaultValue: i18n.language === 'ar' ? 'مشاركة' : 'Share' })}
                title={t('search.card.share', { defaultValue: i18n.language === 'ar' ? 'مشاركة' : 'Share' })}
              >
                <i className="ri-share-line text-xl text-gray-600 hover:text-[#004D40]"></i>
              </button>

              {/* Favorite Button */}
              <button
                onClick={handleFavoriteClick}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-lg backdrop-blur-md border border-white/50 ${
                  propertyIsFavorite
                    ? 'bg-[#DF2F2B] hover:bg-[#B71C1C]'
                    : 'bg-white/95 hover:bg-white'
                }`}
                aria-label={propertyIsFavorite ? t('search.card.removeFromFavorites') : t('search.card.addToFavorites')}
                title={propertyIsFavorite ? t('search.card.removeFromFavorites') : t('search.card.addToFavorites')}
              >
                <i className={`${propertyIsFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-xl ${propertyIsFavorite ? 'text-white' : 'text-gray-600 hover:text-[#DF2F2B]'}`}></i>
              </button>
            </div>
          </div>

        </div>

        {/* Price & Location Badge - Fixed at bottom */}
        <div className="card-price-corner" aria-label={priceAccessible}>
          <div className="flex items-center gap-1.5">
            {onRequest ? (
              <span className="price-amount">{priceOnRequestLabel(i18n.dir() === 'rtl')}</span>
            ) : (
              <>
                <SaudiRiyalSymbol className="h-4 w-4 text-[#004D40]" aria-hidden />
                <span className="price-amount">{formattedPrice}</span>
                {property.listingType?.toUpperCase() === 'RENT' && (
                  <span className="price-period">
                    /{property.rentPeriod === 'yearly' ? t('common:units.year') : t('common:units.month')}
                  </span>
                )}
              </>
            )}
          </div>
          <span className="price-location-tag">{city}</span>
        </div>

        {/* Quick contact buttons (Call + WhatsApp) — surfaced on the card so a
            user can reach out without opening the listing. Sits in the bottom
            corner opposite the price; hidden while the details panel is open and
            only shown when we actually have a phone number. */}
        {!isExpanded && property.agentPhone && (
          <div className={`absolute bottom-4 z-20 flex items-center gap-2 ${isRtl ? 'left-4' : 'right-4'}`}>
            <a
              href={`tel:${property.agentPhone}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!guardContact('call')) e.preventDefault();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={t('property.contact.call', { defaultValue: i18n.language === 'ar' ? 'اتصال' : 'Call' })}
              className="w-11 h-11 rounded-full bg-white/95 shadow-lg border border-white/50 flex items-center justify-center text-[#003930] hover:bg-white hover:scale-105 transition-all"
            >
              <i className="ri-phone-fill text-lg"></i>
            </a>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (guardContact('whatsapp')) {
                  window.open(`https://wa.me/${(property.agentPhone || '').replace(/[^0-9]/g, '')}`, '_blank');
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="WhatsApp"
              // THEME.md: WhatsApp keeps its green as the glyph, never as a fill
              // (the same white circle as the call button beside it).
              className="w-11 h-11 rounded-full bg-white/95 shadow-lg border border-white/50 flex items-center justify-center text-[#1DA851] hover:bg-white hover:scale-105 transition-all"
            >
              <i className="ri-whatsapp-line text-lg"></i>
            </button>
          </div>
        )}

        {/* Media badges — show what this listing includes: photos, video,
            virtual tour, floor plan (mirrors the property detail page). The 360°
            chip stays clickable to open the virtual-tour modal. */}
        {(hasVideo || hasVirtualTour || hasFloorPlan) && (
          <div className="absolute bottom-16 left-3 z-20 flex items-center gap-1.5" dir="ltr">
            {hasVideo && (
              <span
                className="liquid-glass-overlay flex items-center justify-center rounded-full px-2.5 py-1.5 text-white"
                title={i18n.language === 'ar' ? 'فيديو' : 'Video'}
              >
                <i className="ri-vidicon-line text-sm"></i>
              </span>
            )}
            {hasVirtualTour && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVirtualTour(true);
                }}
                className="liquid-glass-overlay flex items-center gap-1 rounded-full px-2.5 py-1.5 text-white text-xs font-medium hover:bg-white/40 transition-colors"
                title={i18n.language === 'ar' ? 'جولة افتراضية' : 'Virtual tour'}
              >
                <i className="ri-panorama-2-line"></i>360°
              </button>
            )}
            {hasFloorPlan && (
              <span
                className="liquid-glass-overlay flex items-center justify-center rounded-full px-2.5 py-1.5 text-white"
                title={i18n.language === 'ar' ? 'المخطط' : 'Floor plan'}
              >
                <i className="ri-ruler-2-line text-sm"></i>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Side Panel (hidden by default, slides in on expand) */}
      <div className="card-side-panel">
        <div className="panel-accent-line"></div>

        {/* Close Button */}
        <button
          type="button"
          onClick={handleExpandToggle}
          className="card-close-btn"
          aria-label={i18n.language === 'ar' ? 'إغلاق' : 'Close'}
        >
          <i className="ri-close-line text-sm"></i>
        </button>

        <div className="card-side-panel-content">
          {/* Tab Navigation - At the very top */}
          <div className="panel-tabs-container">
            <button
              type="button"
              onClick={(e) => handleTabClick('features', e)}
              className={`panel-tab ${activeTab === 'features' ? 'panel-tab-active' : ''}`}
            >
              <i className="ri-sparkling-line"></i>
              <span>{i18n.language === 'ar' ? 'المزايا' : 'Features'}</span>
            </button>
            <button
              type="button"
              onClick={(e) => handleTabClick('agent', e)}
              className={`panel-tab ${activeTab === 'agent' ? 'panel-tab-active' : ''}`}
            >
              <i className="ri-user-star-line"></i>
              <span>{t('search.card.advertiser')}</span>
            </button>
            <button
              type="button"
              onClick={(e) => handleTabClick('location', e)}
              className={`panel-tab ${activeTab === 'location' ? 'panel-tab-active' : ''}`}
            >
              <i className="ri-map-2-line"></i>
              <span>{i18n.language === 'ar' ? 'الموقع' : 'Map'}</span>
            </button>
          </div>

          {/* Property Info Header - Below tabs */}
          <div className="panel-info-header">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-[var(--pv-color-surface-sunken)]/90 text-[#003930]">
                <DomainIcon id={typeIconId} size={14} className="shrink-0" />
                {typeLabel}
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 line-clamp-1 mb-1">{title}</h3>
            <p className="flex items-center gap-1.5 text-sm text-gray-500">
              <i className="ri-map-pin-2-fill text-[#004D40] text-base"></i>
              <span className="line-clamp-1">{city}, {state}</span>
            </p>
          </div>

          {/* Tab Content - scrollable with mouse wheel */}
          <div className="panel-tab-content" onWheel={(e) => e.stopPropagation()}>
            {/* Features Tab - Combined Specs & Amenities Side by Side */}
            {activeTab === 'features' && (
              <div className="features-layout">
                {/* Specs Column */}
                <div className="specs-column">
                  <div className="column-header">
                    <i className="ri-home-gear-line"></i>
                    <span>{i18n.language === 'ar' ? 'المواصفات' : 'Specs'}</span>
                  </div>
                  <div className="specs-grid">
                    {/* Dynamic attributes based on property type */}
                    {displayAttributes.map((attr) => (
                      <div key={attr.key} className="panel-spec-item">
                        <div className="spec-icon-mini">
                          {getAttributeIcon(attr)}
                        </div>
                        <div>
                          <span className="spec-value-mini">
                            {attr.key === 'area'
                              ? formattedArea
                              : formatAttributeValue(attr.key, attr.value, isArabic ? 'ar' : 'en')}
                          </span>
                          <span className="spec-label-mini">
                            {attr.key === 'area' ? unitLabel : attr.label}
                          </span>
                        </div>
                      </div>
                    ))}
                    {/* Show message if no specs available (e.g., for land) */}
                    {displayAttributes.length === 0 && (
                      <div className="col-span-2 text-center text-gray-400 text-sm py-2">
                        {i18n.language === 'ar' ? 'لا توجد مواصفات' : 'No specs available'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Amenities Column */}
                <div className="amenities-column">
                  <div className="column-header">
                    <i className="ri-checkbox-circle-line"></i>
                    <span>{i18n.language === 'ar' ? 'المرافق' : 'Amenities'}</span>
                  </div>
                  <div className="amenities-list">
                    {property.furnished && property.listingType?.toUpperCase() !== 'SALE' && (
                      <span className="panel-amenity-tag">
                        <i className="ri-home-smile-line"></i>
                        {t('search.card.furnished')}
                      </span>
                    )}
                    {property.petFriendly && property.listingType?.toUpperCase() !== 'SALE' && (
                      <span className="panel-amenity-tag">
                        <i className="ri-bear-smile-line"></i>
                        {t('search.card.petFriendly')}
                      </span>
                    )}
                    {property.parking && (
                      <span className="panel-amenity-tag">
                        <i className="ri-parking-box-line"></i>
                        {i18n.language === 'ar' ? 'موقف' : 'Parking'}
                      </span>
                    )}
                    {property.amenities.slice(0, 5).map((amenity, idx) => (
                      <span key={idx} className="panel-amenity-tag">
                        <i className="ri-check-line"></i>
                        {translateAmenity(amenity, i18n.language)}
                      </span>
                    ))}
                    {!property.furnished && !property.petFriendly && !property.parking && property.amenities.length === 0 && (
                      <span className="text-gray-400 text-sm italic">{i18n.language === 'ar' ? 'لا توجد' : 'None'}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Agent Tab - Broker Name & Contact */}
            {activeTab === 'agent' && (
              <div className="space-y-3">
                {/* Agent / company profile — company-owned listings credit the company */}
                <div className="flex items-center gap-2.5 p-2.5 bg-[var(--pv-color-surface-soft)] rounded-lg border border-[var(--pv-border-soft)]">
                  <div className="w-10 h-10 rounded-full bg-[#004D40] flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                    {((property.companyId
                      ? (isArabic ? (property.companyNameAr || property.companyName) : property.companyName)
                      : property.agentName) || 'A').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {property.companyId
                        ? ((isArabic ? (property.companyNameAr || property.companyName) : property.companyName)
                          || (i18n.language === 'ar' ? 'شركة عقارية' : 'Real estate company'))
                        : (property.agentName || t('search.card.advertiser'))}
                    </p>
                    {/* No verification badge here: the search payload carries no
                        verified flag, so a "verified agent" line or a gold check would
                        be a claim we cannot back (RESTRICTIONS §2). */}
                    {property.companyId && property.companySlug ? (
                      <Link
                        to={`/companies/${property.companySlug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#004D40] flex items-center gap-1 hover:underline"
                      >
                        <i className="ri-building-2-line text-xs" aria-hidden="true"></i>
                        {i18n.language === 'ar' ? 'عرض ملف الشركة' : 'View Profile'}
                      </Link>
                    ) : null}
                  </div>
                </div>

                {/* Contact Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {property.agentPhone && (
                    <a
                      href={`tel:${property.agentPhone}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!guardContact('call')) e.preventDefault();
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white border border-gray-200 text-xs text-gray-700 hover:bg-[var(--pv-color-surface-sunken)] hover:border-[var(--pv-border-strong)] transition"
                    >
                      <i className="ri-phone-line text-[#004D40]"></i>
                      <span className="truncate">{property.agentPhone}</span>
                    </a>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (guardContact('whatsapp')) {
                        window.open(`https://wa.me/${(property.agentPhone || '').replace(/[^0-9]/g, '')}`, '_blank');
                      }
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700 font-medium hover:bg-green-100 transition"
                  >
                    <i className="ri-whatsapp-line text-green-600"></i>
                    <span>{i18n.language === 'ar' ? 'واتساب' : 'WhatsApp'}</span>
                  </button>
                </div>
                {property.agentEmail && (
                  <a
                    href={`mailto:${property.agentEmail}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!guardContact('email')) e.preventDefault();
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white border border-gray-200 text-xs text-gray-700 hover:bg-[var(--pv-color-surface-sunken)] hover:border-[var(--pv-border-strong)] transition w-full"
                  >
                    <i className="ri-mail-line text-[#004D40]"></i>
                    <span className="truncate">{property.agentEmail}</span>
                  </a>
                )}
              </div>
            )}

            {/* Location/Map Tab */}
            {activeTab === 'location' && (
              <div className="map-tab-content" onClick={(e) => e.stopPropagation()}>
                {/* Map Preview */}
                <div className="panel-map-full">
                  {property.latitude && property.longitude ? (
                    <GoogleMap
                      latitude={property.latitude}
                      longitude={property.longitude}
                      title={property.title || 'Property Location'}
                      zoom={15}
                      height="200px"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-50 rounded-xl">
                      <div className="text-center text-slate-400">
                        <i className="ri-map-pin-line text-3xl mb-1"></i>
                        <p className="text-xs">{i18n.language === 'ar' ? 'الموقع غير متوفر' : 'Location unavailable'}</p>
                      </div>
                    </div>
                  )}
                </div>
                {/* Location info + Directions inline */}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <i className="ri-map-pin-2-fill text-[#004D40] text-sm"></i>
                      <span className="truncate">{city}, {state}</span>
                    </div>
                    {hasDistanceInfo && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                        <i className="ri-route-line text-purple-400 text-sm"></i>
                        <span>{property.distanceFromLandmark?.toFixed(1)}{' '}km {i18n.language === 'ar' ? 'من' : 'from'} {landmarkName}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`, '_blank');
                    }}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[var(--pv-color-surface-sunken)] text-[#003930] text-xs font-medium hover:bg-[#DDEEEB] transition flex-shrink-0"
                  >
                    <i className="ri-direction-line text-sm"></i>
                    <span>{i18n.language === 'ar' ? 'اتجاهات' : 'Directions'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer: CTA Button */}
          <div className="panel-footer">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              disabled={isActionDisabled}
              className="btn-glass-dark w-full justify-center"
            >
              <i className="ri-eye-line"></i>
              <span>{actionLabel}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Virtual Tour Modal */}
      {hasVirtualTour && (
        <VirtualTourModal
          isOpen={showVirtualTour}
          onClose={() => setShowVirtualTour(false)}
          imageUrl={property.virtualTourUrl!}
          propertyTitle={title}
        />
      )}

      {/* List Picker Modal */}
      <ListPickerModal
        isOpen={showListPicker}
        onClose={handleListPickerClose}
        propertyId={property.id}
        propertyTitle={title}
        propertyImageUrl={property.imageUrl || (property.galleryImages && property.galleryImages[0])}
        onSaved={dismissSaveHint}
      />

      {/* Sign-in required before contacting a broker (guests only) */}
      {showSignInPrompt && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          dir={i18n.dir()}
          onClick={(e) => { e.stopPropagation(); setShowSignInPrompt(false); }}
          role="dialog"
          aria-modal="true"
          aria-label={t('property:property.contact.authPrompt.title')}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[#004D40]">
              <i className="ri-lock-2-line text-3xl"></i>
            </div>
            <h3 className="m-0 mb-2 text-lg font-black text-[var(--pv-color-text)]">{t('property:property.contact.authPrompt.title')}</h3>
            <p className="m-0 mb-5 text-sm text-[#617672]">{t('property:property.contact.authPrompt.message')}</p>
            <div className="flex flex-col gap-2.5">
              <button
                ref={signInPrimaryRef}
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate('/signin', { state: { from: location } }); }}
                className="w-full rounded-xl bg-[#1B7566] px-4 py-3 text-sm font-extrabold text-white transition hover:opacity-90"
              >
                {t('property:property.contact.authPrompt.signIn')}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowSignInPrompt(false); }}
                className="w-full rounded-xl bg-[var(--pv-color-surface-sunken)] px-4 py-3 text-sm font-bold text-[#617672] transition hover:bg-[#E0E9E7]"
              >
                {t('property:property.contact.authPrompt.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default memo(PropertyCard);
